import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  "aria-label": "breadcrumb",
  class: "mt-5 pt-5 ps-3"
}
const _hoisted_2 = { class: "breadcrumb mb-3" }
const _hoisted_3 = { class: "breadcrumb-item" }
const _hoisted_4 = { class: "container-fluid" }
const _hoisted_5 = { class: "row" }
const _hoisted_6 = { class: "col-sm-12" }
const _hoisted_7 = { class: "card" }
const _hoisted_8 = { class: "card-header d-flex justify-content-between align-items-center" }
const _hoisted_9 = { class: "d-flex gap-2" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "card-body" }
const _hoisted_13 = { class: "table-responsive theme-scrollbar" }
const _hoisted_14 = {
  class: "table table-hover align-middle",
  style: {"min-width":"1400px"}
}
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { key: 1 }
const _hoisted_17 = {
  colspan: 8,
  class: "text-center py-4"
}
const _hoisted_18 = { class: "text-center py-5" }
const _hoisted_19 = { class: "mt-3" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { key: 2 }
const _hoisted_22 = { class: "text-muted small" }
const _hoisted_23 = {
  key: 0,
  class: "badge bg-secondary text-capitalize"
}
const _hoisted_24 = {
  key: 1,
  class: "text-muted small"
}
const _hoisted_25 = { class: "badge bg-info" }
const _hoisted_26 = { class: "d-flex gap-1 flex-wrap" }
const _hoisted_27 = ["title"]
const _hoisted_28 = { class: "text-muted" }
const _hoisted_29 = { class: "d-flex gap-1 justify-content-end" }
const _hoisted_30 = ["onClick", "disabled"]
const _hoisted_31 = ["onClick", "disabled"]
const _hoisted_32 = ["onClick", "title", "disabled"]
const _hoisted_33 = ["onClick", "disabled"]
const _hoisted_34 = {
  key: 0,
  class: "modal fade show d-block",
  tabindex: "-1",
  style: {"background-color":"rgba(0, 0, 0, 0.5)"}
}
const _hoisted_35 = { class: "modal-dialog modal-dialog-centered" }
const _hoisted_36 = { class: "modal-content" }
const _hoisted_37 = { class: "modal-header" }
const _hoisted_38 = { class: "modal-body" }
const _hoisted_39 = { class: "modal-footer" }
const _hoisted_40 = ["disabled"]
const _hoisted_41 = { key: 0 }
const _hoisted_42 = { key: 1 }

import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

interface KnowledgeBase {
  _id: string;
  name: string;
  description?: string;
  is_active: boolean;
  document_count?: number;
  sourceTypes?: {
    file?: number;
    url?: number;
    text?: number;
  };
  source_type?: string,
  category?: {_id?: string, name?: string
},
  created_at: string;
  updatedAt: string;
}

// Reactive data

export default /*@__PURE__*/_defineComponent({
  __name: 'KnowledgeBasesList',
  setup(__props) {

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

// Types
const knowledgeBases = ref<KnowledgeBase[]>([]);
const isLoading = ref(false);
const isDeleting = ref(false);
const error = ref<string | null>(null);
const showDeleteModal = ref(false);
const selectedKB = ref<KnowledgeBase | null>(null);
const router = useRouter();

// Methods
const loadKnowledgeBases = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await apiService.getKnowledgeBases();

    if (Array.isArray(response)) {
      knowledgeBases.value = response;
    } else if (response.data && Array.isArray(response.data)) {
      knowledgeBases.value = response.data;
    } else {
      knowledgeBases.value = [];
    }
  } catch (err: any) {
    console.error("Error loading knowledge bases:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load knowledge bases";
    showToast("Error loading knowledge bases", "error");
  } finally {
    isLoading.value = false;
  }
};

const toggleStatus = async (kb: KnowledgeBase) => {
   const payload = { 
      is_active: !kb.is_active,
    };
  try {
    await apiService.updateKnowledgeBase(kb._id, payload);
    kb.is_active = !kb.is_active;
    showToast(
      `Knowledge base ${
        kb.is_active ? "activated" : "deactivated"
      } successfully`,
      "success"
    );
  } catch (err: any) {
    console.error("Error toggling status:", err);
    showToast(
      err?.response?.data?.message || "Failed to update status",
      "error"
    );
  }
};

const confirmDelete = (kb: KnowledgeBase) => {
  selectedKB.value = kb;
  showDeleteModal.value = true;
};

const deleteKnowledgeBase = async () => {
  if (!selectedKB.value) return;

  isDeleting.value = true;
  try {
    await apiService.deleteKnowledgeBase(selectedKB.value._id);
    knowledgeBases.value = knowledgeBases.value.filter(
      (kb) => kb._id !== selectedKB.value?._id
    );
    showDeleteModal.value = false;
    showToast("Knowledge base deleted successfully", "success");
  } catch (err: any) {
    console.error("Error deleting knowledge base:", err);
    showToast(
      err?.response?.data?.message || "Failed to delete knowledge base",
      "error"
    );
  } finally {
    isDeleting.value = false;
    selectedKB.value = null;
  }
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC", // ensures no date shift from Z timezone
  }).format(date);
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  // Simple toast implementation - you can replace with your toast library
  console.log(`[${type.toUpperCase()}] ${message}`);

  // You can integrate with a toast library like vue-toastification here
  // For now, using alert as fallback
  if (type === "error") {
    alert(message);
  }
};

// Lifecycle
onMounted(() => {
  loadKnowledgeBases();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("nav", _hoisted_1, [
      _createElementVNode("ol", _hoisted_2, [
        _createElementVNode("li", _hoisted_3, [
          _createVNode(_component_router_link, { to: "/" }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode("Home", -1)
            ]))]),
            _: 1
          })
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("li", {
          class: "breadcrumb-item active",
          "aria-current": "page"
        }, " Knowledge Bases ", -1))
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[8] || (_cache[8] = _createElementVNode("div", null, [
                _createElementVNode("h5", { class: "mb-1" }, "Knowledge Bases"),
                _createElementVNode("small", { class: "text-muted" }, " Manage your knowledge bases and document collections ")
              ], -1)),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("button", {
                  class: "btn btn-outline-secondary btn-sm",
                  onClick: loadKnowledgeBases,
                  disabled: isLoading.value
                }, [...(_cache[6] || (_cache[6] = [
                  _createElementVNode("i", { class: "fa fa-refresh me-1" }, null, -1),
                  _createTextVNode(" Refresh ", -1)
                ]))], 8, _hoisted_10),
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push('/admin/knowledge-bases/new'))),
                  class: "btn btn-primary btn-sm",
                  disabled: !_unref(canCreate)('knowledge bases')
                }, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("i", { class: "fa fa-plus me-1" }, null, -1),
                  _createTextVNode(" Create Knowledge Base ", -1)
                ]))], 8, _hoisted_11)
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("table", _hoisted_14, [
                  _cache[17] || (_cache[17] = _createElementVNode("thead", { class: "table-dark" }, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", { style: {"min-width":"200px"} }, "Name"),
                      _createElementVNode("th", { style: {"min-width":"200px"} }, "Description"),
                      _createElementVNode("th", { style: {"width":"150px"} }, "Category"),
                      _createElementVNode("th", { style: {"width":"130px"} }, "Documents"),
                      _createElementVNode("th", { style: {"width":"200px"} }, "Source Types"),
                      _createElementVNode("th", { style: {"width":"200px"} }, "Status"),
                      _createElementVNode("th", { style: {"width":"200px"} }, "Created"),
                      _createElementVNode("th", { style: {"width":"200px"} }, "Actions")
                    ])
                  ], -1)),
                  (isLoading.value)
                    ? (_openBlock(), _createElementBlock("tbody", _hoisted_15, [...(_cache[9] || (_cache[9] = [
                        _createElementVNode("tr", null, [
                          _createElementVNode("td", {
                            colspan: 8,
                            class: "text-center py-4"
                          }, [
                            _createElementVNode("div", {
                              class: "spinner-border text-primary",
                              role: "status"
                            }, [
                              _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                            ])
                          ])
                        ], -1)
                      ]))]))
                    : (knowledgeBases.value.length < 1)
                      ? (_openBlock(), _createElementBlock("tbody", _hoisted_16, [
                          _createElementVNode("tr", null, [
                            _createElementVNode("td", _hoisted_17, [
                              _createElementVNode("div", _hoisted_18, [
                                _cache[11] || (_cache[11] = _createElementVNode("div", { class: "mb-3" }, [
                                  _createElementVNode("i", { class: "fa fa-database fa-3x text-muted" })
                                ], -1)),
                                _cache[12] || (_cache[12] = _createElementVNode("h5", { class: "text-muted" }, "No Knowledge Bases Found", -1)),
                                _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-muted" }, " Create your first knowledge base to get started. ", -1)),
                                _createElementVNode("div", _hoisted_19, [
                                  _createElementVNode("button", {
                                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push('/admin/knowledge-bases/new'))),
                                    class: "btn btn-primary",
                                    disabled: !_unref(canCreate)('knowledge bases')
                                  }, [...(_cache[10] || (_cache[10] = [
                                    _createElementVNode("i", { class: "fa fa-plus me-1" }, null, -1),
                                    _createTextVNode(" Create Knowledge Base ", -1)
                                  ]))], 8, _hoisted_20)
                                ])
                              ])
                            ])
                          ])
                        ]))
                      : (knowledgeBases.value.length > 0)
                        ? (_openBlock(), _createElementBlock("tbody", _hoisted_21, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(knowledgeBases.value, (kb) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: kb._id
                              }, [
                                _createElementVNode("td", null, [
                                  _createElementVNode("strong", null, _toDisplayString(kb.name), 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("span", _hoisted_22, _toDisplayString(kb.description || "No description"), 1)
                                ]),
                                _createElementVNode("td", null, [
                                  (kb.category)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(kb.category?.name), 1))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_24, "-"))
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("span", _hoisted_25, _toDisplayString(kb.document_count || 0), 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_26, [
                                    (kb && kb.source_type && kb.source_type.trim())
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 0,
                                          class: "badge bg-primary",
                                          title: kb.source_type
                                        }, _toDisplayString(kb.source_type), 9, _hoisted_27))
                                      : _createCommentVNode("", true)
                                  ])
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("span", {
                                    class: _normalizeClass(
                            kb.is_active
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          )
                                  }, _toDisplayString(kb.is_active ? "Active" : "Inactive"), 3)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("small", _hoisted_28, _toDisplayString(formatDate(kb.created_at)), 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_29, [
                                    _createElementVNode("button", {
                                      onClick: ($event: any) => (_unref(router).push(`/admin/knowledge-bases/${kb._id}`)),
                                      class: "btn btn-sm btn-outline-primary",
                                      title: "View Details",
                                      disabled: !_unref(canView)('knowledge bases')
                                    }, [...(_cache[14] || (_cache[14] = [
                                      _createElementVNode("i", { class: "fa fa-eye" }, null, -1)
                                    ]))], 8, _hoisted_30),
                                    _createElementVNode("button", {
                                      onClick: ($event: any) => (_unref(router).push(`/admin/knowledge-bases/${kb._id}/edit`)),
                                      class: "btn btn-sm btn-outline-secondary",
                                      title: "Edit",
                                      disabled: !_unref(canUpdate)('knowledge bases')
                                    }, [...(_cache[15] || (_cache[15] = [
                                      _createElementVNode("i", { class: "fa fa-edit" }, null, -1)
                                    ]))], 8, _hoisted_31),
                                    _createElementVNode("button", {
                                      onClick: ($event: any) => (toggleStatus(kb)),
                                      class: "btn btn-sm btn-outline-warning",
                                      title: kb.is_active ? 'Deactivate' : 'Activate',
                                      disabled: !_unref(canUpdate)('knowledge bases')
                                    }, [
                                      _createElementVNode("i", {
                                        class: _normalizeClass(
                                kb.is_active ? 'fa fa-pause' : 'fa fa-play'
                              )
                                      }, null, 2)
                                    ], 8, _hoisted_32),
                                    _createElementVNode("button", {
                                      onClick: ($event: any) => (confirmDelete(kb)),
                                      class: "btn btn-sm btn-outline-danger",
                                      title: "Delete",
                                      disabled: !_unref(canDelete)('knowledge bases')
                                    }, [...(_cache[16] || (_cache[16] = [
                                      _createElementVNode("i", { class: "fa fa-trash" }, null, -1)
                                    ]))], 8, _hoisted_33)
                                  ])
                                ])
                              ]))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                ])
              ])
            ])
          ])
        ])
      ])
    ]),
    (showDeleteModal.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
          _createElementVNode("div", _hoisted_35, [
            _createElementVNode("div", _hoisted_36, [
              _createElementVNode("div", _hoisted_37, [
                _cache[18] || (_cache[18] = _createElementVNode("h5", { class: "modal-title" }, "Confirm Delete", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-close",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (showDeleteModal.value = false))
                })
              ]),
              _createElementVNode("div", _hoisted_38, [
                _createElementVNode("p", null, [
                  _cache[19] || (_cache[19] = _createTextVNode(" Are you sure you want to delete the knowledge base ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(selectedKB.value?.name), 1),
                  _cache[20] || (_cache[20] = _createTextVNode("? ", -1))
                ]),
                _cache[21] || (_cache[21] = _createElementVNode("p", { class: "text-danger small mb-0" }, [
                  _createElementVNode("i", { class: "fa fa-exclamation-triangle me-1" }),
                  _createTextVNode(" This action cannot be undone. All documents will be removed. ")
                ], -1))
              ]),
              _createElementVNode("div", _hoisted_39, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-secondary",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (showDeleteModal.value = false))
                }, " Cancel "),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-danger",
                  onClick: deleteKnowledgeBase,
                  disabled: isDeleting.value
                }, [
                  (isDeleting.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_41, [...(_cache[22] || (_cache[22] = [
                        _createElementVNode("span", {
                          class: "spinner-border spinner-border-sm me-1",
                          role: "status"
                        }, null, -1),
                        _createTextVNode(" Deleting... ", -1)
                      ]))]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_42, [...(_cache[23] || (_cache[23] = [
                        _createElementVNode("i", { class: "fa fa-trash me-1" }, null, -1),
                        _createTextVNode(" Delete ", -1)
                      ]))]))
                ], 8, _hoisted_40)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})